import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import UserProfile from './UserProfile';
import './App.css';

// Smart month detection utility for sorting
const getMonthSortValue = (name) => {
  if (!name || typeof name !== 'string') return name;
  
  const monthMap = {
    'january': '01', 'jan': '01',
    'february': '02', 'feb': '02',
    'march': '03', 'mar': '03',
    'april': '04', 'apr': '04',
    'may': '05',
    'june': '06', 'jun': '06',
    'july': '07', 'jul': '07',
    'august': '08', 'aug': '08',
    'september': '09', 'sep': '09', 'sept': '09',
    'october': '10', 'oct': '10',
    'november': '11', 'nov': '11',
    'december': '12', 'dec': '12'
  };
  
  const lowerName = name.toLowerCase();
  
  // Check if the name contains any month name/abbreviation
  for (const [monthName, monthValue] of Object.entries(monthMap)) {
    if (lowerName.includes(monthName)) {
      // Replace the month name with the month number for sorting
      // This creates a sort key while preserving the original display name
      return lowerName.replace(monthName, monthValue);
    }
  }
  
  // No month detected, return original name for regular alphabetical sorting
  return lowerName;
};

// Mandatory Password Change Component
const MandatoryPasswordChange = ({ user, onPasswordChanged }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requirements, setRequirements] = useState({
    minLength: 6,
    complexity: 'basic'
  });

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        setRequirements({
          minLength: 6,
          complexity: 'basic'
        });
      } catch (err) {
        console.error('Failed to fetch password requirements');
      }
    };
    
    fetchRequirements();
  }, []);

  const validatePassword = (password) => {
    if (password.length < requirements.minLength) {
      return `Password must be at least ${requirements.minLength} characters long`;
    }
    
    if (requirements.complexity === 'medium' && !/^(?=.*[A-Za-z])(?=.*\d)/.test(password)) {
      return 'Password must contain both letters and numbers';
    }
    
    if (requirements.complexity === 'strong' && !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])/.test(password)) {
      return 'Password must contain letters, numbers, and special characters (@$!%*#?&)';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/user/mandatory-password-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        },
        body: JSON.stringify({ new_password: newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        onPasswordChanged();
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getComplexityDescription = () => {
    switch (requirements.complexity) {
      case 'medium':
        return 'Must contain letters and numbers';
      case 'strong':
        return 'Must contain letters, numbers, and special characters (@$!%*#?&)';
      default:
        return 'Basic password requirements';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md mx-auto shadow-2xl">
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">🔐</div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Password Change Required
            </h2>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
              Welcome, {user.first_name || user.username}! For security reasons, you must change your password before accessing the vault.
            </p>
          </div>

          {/* Warning Box */}
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-lg mb-6 text-sm">
            <strong>Important:</strong> This is a one-time security requirement. After changing your password, you'll have full access to the document vault.
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Password Requirements */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="font-semibold text-gray-900 text-sm mb-2">Password Requirements:</div>
            <div className="space-y-1 text-sm text-gray-600">
              <div>• Minimum {requirements.minLength} characters</div>
              <div>• {getComplexityDescription()}</div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field text-base"
              required
            />

            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field text-base"
              required
            />

            <button
              type="submit"
              disabled={loading}
              className={`w-full btn-primary py-3 text-base ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Password Reset Request Component
const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/auth/request-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to send reset email');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <div className="card">
            <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-6">Check Your Email</h2>
            <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg mb-6 text-sm">
              If an account with that email address exists, we've sent you a password reset link.
            </div>
            <p className="text-center text-gray-600 mb-6 text-sm">
              The link will expire in 60 minutes for security.
            </p>
            <button onClick={onBack} className="w-full btn-secondary">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="card">
          <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-6">Reset Password</h2>
          <p className="text-center text-gray-600 mb-6 text-sm">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              required
            />

            <button
              type="submit"
              disabled={loading}
              className={`w-full btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <button onClick={onBack} className="w-full btn-secondary mt-4">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

// Password Reset Component
const ResetPassword = ({ token, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess();
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        <div className="card">
          <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-6">Set New Password</h2>
          <p className="text-center text-gray-600 mb-6 text-sm">
            Enter your new password below.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input-field"
              required
            />

            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field"
              required
            />

            <button
              type="submit"
              disabled={loading}
              className={`w-full btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Login Component
const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => {
    localStorage.removeItem('rwcvault_token');
    localStorage.removeItem('rwcvault_remember');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...credentials,
          remember: rememberMe
        })
      });

      const data = await response.json();

      if (response.ok) {

        localStorage.removeItem('rwcvault_token');
        localStorage.removeItem('rwcvault_remember');

        localStorage.setItem('rwcvault_token', data.token);
        if (rememberMe) {
          localStorage.setItem('rwcvault_remember', 'true');
        }

        onLogin(data.user, data.token);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md">
        {/* Logo Container */}
        <div className="text-center mb-8">
          <img
            src={`${window.rwcvaultData.pluginUrl}frontend/build/rwc-logo.png`}
            alt="Rossmoor Walnut Creek"
            className="h-20 w-auto mx-auto"
            onError={(e) => {
              console.error('Logo failed to load from:', e.target.src);
              e.target.style.display = 'none';
            }}
          />
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900">Mutual Vault Login</h2>
        </div>

        <div className="card">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="input-field"
              required
            />

            <input
              type="password"
              placeholder="Password"
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit(e)}
              className="input-field"
              required
            />

            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-rwc-blue focus:ring-rwc-blue border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                Remember me for 7 days
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full btn-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Logging in...' : 'Sign In'}
            </button>
          </form>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-rwc-blue hover:text-rwc-blue-dark underline"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Navigation Component
const MobileNav = ({ onMenuClick, onProfileClick, onAdminClick, onLogout, isAdmin }) => {
  return (
    <div className="mobile-nav md:hidden">
      <div className="flex justify-around items-center">
        <button
          onClick={onMenuClick}
          className="flex flex-col items-center p-2 text-gray-600 hover:text-rwc-blue"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span className="text-xs mt-1">Menu</span>
        </button>
        
        <button
          onClick={onProfileClick}
          className="flex flex-col items-center p-2 text-gray-600 hover:text-rwc-blue"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-xs mt-1">Profile</span>
        </button>
        
        {isAdmin && (
          <button
            onClick={onAdminClick}
            className="flex flex-col items-center p-2 text-gray-600 hover:text-rwc-blue"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-xs mt-1">Admin</span>
          </button>
        )}
        
        <button
          onClick={onLogout}
          className="flex flex-col items-center p-2 text-gray-600 hover:text-red-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="text-xs mt-1">Logout</span>
        </button>
      </div>
    </div>
  );
};

// Document Portal Component - Mobile Responsive Version
const DocumentPortal = ({ user, onLogout, isAdmin, onAdminClick, onProfileClick }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showingRootFolders, setShowingRootFolders] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initial load
  useEffect(() => {
    fetchFolders();
    setSelectedFolder(null);
    setShowingRootFolders(true);
  }, [user]);

  // Auto-expand path when folders are loaded and we have a selected folder
  useEffect(() => {
    if (folders.length > 0 && selectedFolder) {
      expandPathToFolder(selectedFolder);
    }
  }, [folders]);

  // Handle visibility change for refresh
  useEffect(() => {
    let lastHiddenTime = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastHiddenTime = Date.now();
      } else if (lastHiddenTime) {
        const hiddenDuration = Date.now() - lastHiddenTime;
        if (hiddenDuration > 300000) {
          fetchFolders();
          if (selectedFolder) {
            fetchDocuments(selectedFolder);
          }
        }
        lastHiddenTime = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedFolder]);

  // Listen for updates from other tabs
  useEffect(() => {
    const channel = new BroadcastChannel('rwcvault_updates');

    channel.onmessage = (event) => {

      if (event.data.type === 'folders_updated' || event.data.type === 'permissions_updated') {
        fetchFolders();
      } else if (event.data.type === 'documents_updated' && selectedFolder) {
        fetchDocuments(selectedFolder);
      }
    };

    return () => channel.close();
  }, [selectedFolder]);

  const fetchFolders = async () => {
    try {

      const response = await fetch(`${window.rwcvaultData.apiUrl}/folders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        const isTreeStructure = data.some(folder => folder.children !== undefined);

        if (isTreeStructure) {
          setFolders(data);
        } else {
          setFolders(data);
        }

        if (selectedFolder && !data.some(f => f.id === selectedFolder)) {
          setSelectedFolder(null);
          setDocuments([]);
        }
      } else {
        console.error('Failed to fetch folders:', response.status);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (folderId) => {
    setLoading(true);
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/folders/${folderId}/documents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
        setSelectedFolder(folderId);
        setShowingRootFolders(false);
        setSidebarOpen(false); // Close sidebar on mobile after selection
        
        // Auto-expand path to show where we are after data loads
        setTimeout(() => {
          expandPathToFolder(folderId);
        }, 50);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSubfolders = (parentId) => {

    const findFolderInTree = (folderList, targetId) => {
      for (const folder of folderList) {
        if (folder.id == targetId || folder.id === targetId) {
          return folder;
        }
        if (folder.children && folder.children.length > 0) {
          const found = findFolderInTree(folder.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const parentFolder = findFolderInTree(folders, parentId);
    if (parentFolder && parentFolder.children) {
      return parentFolder.children;
    }

    const subfolders = folders.filter(folder => {
      return folder.parent_id == parentId ||
        folder.parent_id === parentId ||
        folder.parent_id === String(parentId) ||
        folder.parent_id === Number(parentId);
    });

    return subfolders;
  };

  const buildFolderTree = (flatFolders) => {
    const folderMap = {};
    const rootFolders = [];

    flatFolders.forEach(folder => {
      folderMap[folder.id] = { ...folder, children: [] };
    });

    flatFolders.forEach(folder => {
      if (folder.parent_id && folderMap[folder.parent_id]) {
        folderMap[folder.parent_id].children.push(folderMap[folder.id]);
      } else if (!folder.parent_id) {
        rootFolders.push(folderMap[folder.id]);
      }
    });

    return rootFolders;
  };

  // Helper function to find folder path from root to target folder
  const findFolderPath = (foldersTree, targetId, path = []) => {
    for (const folder of foldersTree) {
      const currentPath = [...path, folder.id];
      
      if (folder.id === targetId) {
        return currentPath;
      }
      
      if (folder.children && folder.children.length > 0) {
        const foundPath = findFolderPath(folder.children, targetId, currentPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }
    return null;
  };

  // Helper function to expand all folders in path to selected folder
  const expandPathToFolder = (folderId) => {
    if (!folderId || !folders.length) return;
    
    const foldersTree = folders.length > 0 && folders[0].children !== undefined 
      ? folders 
      : buildFolderTree(folders);
    
    const pathToFolder = findFolderPath(foldersTree, folderId);
    
    if (pathToFolder) {
      setExpandedFolders(prev => {
        const newExpanded = new Set(prev);
        // Expand all folders in the path except the target folder itself
        pathToFolder.slice(0, -1).forEach(folderId => {
          newExpanded.add(folderId);
        });
        return newExpanded;
      });
    }
  };

  // Toggle folder expansion
  const toggleFolderExpansion = (folderId) => {
    setExpandedFolders(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      return newExpanded;
    });
  };

  // Build breadcrumb path from root to current folder
  const buildBreadcrumbPath = (targetFolderId) => {
    if (!targetFolderId || !folders.length) return [];
    
    const foldersTree = folders.length > 0 && folders[0].children !== undefined 
      ? folders 
      : buildFolderTree(folders);
    
    const findFolderWithPath = (foldersArray, targetId, path = []) => {
      for (const folder of foldersArray) {
        const currentPath = [...path, { id: folder.id, name: folder.name }];
        
        if (folder.id === targetId) {
          return currentPath;
        }
        
        if (folder.children && folder.children.length > 0) {
          const foundPath = findFolderWithPath(folder.children, targetId, currentPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };
    
    return findFolderWithPath(foldersTree, targetFolderId) || [];
  };

  const handleFolderClick = (folderId) => {
    fetchDocuments(folderId);
    // Auto-expand path to show where we are
    expandPathToFolder(folderId);
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
    setDocuments([]);
    setShowingRootFolders(true);
  };

  const handleDirectDownload = async (documentInfo = {}) => {
    // Check if file is large (> 10MB) to show progress
    const fileSize = documentInfo.file_size || 0;
    const showProgress = fileSize > 10 * 1024 * 1024; // 10MB threshold
    
    let progressModal = null;
    let progressBar = null;
    let progressText = null;
    let blobUrl = null; // Store blob URL for completion
    
    if (showProgress) {
      // Create progress modal for large files
      progressModal = document.createElement('div');
      progressModal.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 8px;
        padding: 24px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        z-index: 3000;
        min-width: 350px;
      `;
      
      const title = document.createElement('h3');
      title.textContent = 'Download Progress';
      title.style.cssText = 'margin-bottom: 16px; font-size: 18px; font-weight: 600;';
      progressModal.appendChild(title);
      
      const fileName = document.createElement('div');
      fileName.textContent = documentInfo.original_name || 'Document';
      fileName.style.cssText = 'font-size: 14px; color: #666; margin-bottom: 12px; word-break: break-word;';
      progressModal.appendChild(fileName);
      
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        width: 100%;
        height: 20px;
        background: #f0f0f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 12px;
      `;
      
      progressBar = document.createElement('div');
      progressBar.style.cssText = `
        height: 100%;
        background: #10b981;
        width: 0%;
        transition: width 0.3s ease;
      `;
      progressContainer.appendChild(progressBar);
      progressModal.appendChild(progressContainer);
      
      progressText = document.createElement('div');
      progressText.style.cssText = 'font-size: 14px; color: #666; text-align: center;';
      progressText.textContent = 'Starting download...';
      progressModal.appendChild(progressText);
      
      // Cancel button
      const cancelButton = document.createElement('button');
      cancelButton.textContent = 'Cancel Download';
      cancelButton.style.cssText = `
        width: 100%;
        background: #6b7280;
        color: white;
        border: none;
        padding: 12px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        margin-top: 16px;
      `;
      
      cancelButton.onclick = () => {
        if (progressModal.parentNode) {
          document.body.removeChild(progressModal);
        }
        if (blobUrl) {
          window.URL.revokeObjectURL(blobUrl);
        }
      };
      
      progressModal.appendChild(cancelButton);
      document.body.appendChild(progressModal);
    }
    
    try {
      const token = localStorage.getItem('rwcvault_token');
      
      if (showProgress) {
        // Use XMLHttpRequest for progress tracking on large files
        const xhr = new XMLHttpRequest();
        
        await new Promise((resolve, reject) => {
          xhr.open('POST', `${window.rwcvaultData.apiUrl}/documents/${documentInfo.id}/download`, true);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.setRequestHeader('X-Download-Action', 'download');
          xhr.responseType = 'blob';
          
          xhr.onprogress = (event) => {
            if (event.lengthComputable && progressBar && progressText) {
              const percentComplete = (event.loaded / event.total) * 100;
              progressBar.style.width = percentComplete + '%';
              
              const mbLoaded = (event.loaded / (1024 * 1024)).toFixed(1);
              const mbTotal = (event.total / (1024 * 1024)).toFixed(1);
              progressText.textContent = `${mbLoaded} MB / ${mbTotal} MB (${Math.round(percentComplete)}%)`;
            }
          };
          
          xhr.onload = () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              blobUrl = window.URL.createObjectURL(blob);
              
              // Directly download the file
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = documentInfo.original_name || 'document';
              
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Update modal and auto-close
              if (progressModal && progressText) {
                progressText.textContent = 'Download complete!';
                setTimeout(() => {
                  if (progressModal.parentNode) {
                    document.body.removeChild(progressModal);
                  }
                }, 1500);
              }
              
              // Clean up after a delay
              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
              
              resolve();
            } else {
              reject(new Error('Download failed'));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send();
        });
      } else {
        // For smaller files, use the simpler fetch approach
        const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${documentInfo.id}/download`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Download-Action': 'download'
          }
        });
        
        if (!response.ok) {
          throw new Error('Download failed');
        }
        
        // Get the blob from response
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = documentInfo.original_name || 'document';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
      
      if (progressModal && progressModal.parentNode) {
        document.body.removeChild(progressModal);
      }
      
      // Clean up blob URL if it exists
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    }
  };

  const viewDocument = async (documentId, documentInfo = {}) => {
    // Check if file is large (> 10MB) to show progress
    const fileSize = documentInfo.file_size || 0;
    const showProgress = fileSize > 10 * 1024 * 1024; // 10MB threshold
    
    let progressModal = null;
    let progressBar = null;
    let progressText = null;
    let blobUrl = null; // Store blob URL for completion buttons
    
    if (showProgress) {
      // Create progress modal for large files
      progressModal = document.createElement('div');
      progressModal.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 8px;
        padding: 24px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        z-index: 3000;
        min-width: 350px;
      `;
      
      const title = document.createElement('h3');
      title.textContent = 'Loading Document';
      title.style.cssText = 'margin-bottom: 16px; font-size: 18px; font-weight: 600;';
      progressModal.appendChild(title);
      
      const fileName = document.createElement('div');
      fileName.textContent = documentInfo.original_name || 'Document';
      fileName.style.cssText = 'font-size: 14px; color: #666; margin-bottom: 12px; word-break: break-word;';
      progressModal.appendChild(fileName);
      
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        width: 100%;
        height: 20px;
        background: #f0f0f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 12px;
      `;
      
      progressBar = document.createElement('div');
      progressBar.style.cssText = `
        height: 100%;
        background: #3b82f6;
        width: 0%;
        transition: width 0.3s ease;
      `;
      progressContainer.appendChild(progressBar);
      progressModal.appendChild(progressContainer);
      
      progressText = document.createElement('div');
      progressText.style.cssText = 'font-size: 14px; color: #666; text-align: center;';
      progressText.textContent = 'Loading...';
      progressModal.appendChild(progressText);
      
      document.body.appendChild(progressModal);
    }
    
    try {
      const token = localStorage.getItem('rwcvault_token');
      
      if (showProgress) {
        // Use XMLHttpRequest for progress tracking on large files
        const xhr = new XMLHttpRequest();
        
        await new Promise((resolve, reject) => {
          xhr.open('POST', `${window.rwcvaultData.apiUrl}/documents/${documentId}/download`, true);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.setRequestHeader('X-Download-Action', 'view');
          xhr.responseType = 'blob';
          
          xhr.onprogress = (event) => {
            if (event.lengthComputable && progressBar && progressText) {
              const percentComplete = (event.loaded / event.total) * 100;
              progressBar.style.width = percentComplete + '%';
              
              const mbLoaded = (event.loaded / (1024 * 1024)).toFixed(1);
              const mbTotal = (event.total / (1024 * 1024)).toFixed(1);
              progressText.textContent = `${mbLoaded} MB / ${mbTotal} MB (${Math.round(percentComplete)}%)`;
            }
          };
          
          xhr.onload = () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              blobUrl = window.URL.createObjectURL(blob);
              
              // Show completion buttons
              if (progressModal && progressText) {
                progressText.textContent = 'Download complete!';
                
                // Hide progress bar
                if (progressBar) {
                  progressBar.parentNode.style.display = 'none';
                }
                
                // Create buttons container
                const buttonsContainer = document.createElement('div');
                buttonsContainer.style.cssText = 'display: flex; gap: 12px; margin-top: 16px;';
                
                // Create "Open Document" button
                const openButton = document.createElement('button');
                const isPDF = blob.type === 'application/pdf';
                openButton.textContent = isPDF ? 'View Document' : 'Download File';
                openButton.style.cssText = `
                  flex: 1;
                  background: #3b82f6;
                  color: white;
                  border: none;
                  padding: 12px 16px;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: 500;
                `;
                
                openButton.onclick = () => {
                  // This is a direct user action - no popup blocker!
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.target = '_blank';
                  
                  if (!isPDF) {
                    link.download = documentInfo.original_name || 'document';
                  }
                  
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  // Close modal and cleanup
                  if (progressModal.parentNode) {
                    document.body.removeChild(progressModal);
                  }
                  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
                };
                
                // Create "Cancel" button
                const cancelButton = document.createElement('button');
                cancelButton.textContent = 'Cancel';
                cancelButton.style.cssText = `
                  flex: 1;
                  background: #6b7280;
                  color: white;
                  border: none;
                  padding: 12px 16px;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: 500;
                `;
                
                cancelButton.onclick = () => {
                  // Cleanup and close
                  if (progressModal.parentNode) {
                    document.body.removeChild(progressModal);
                  }
                  if (blobUrl) {
                    window.URL.revokeObjectURL(blobUrl);
                  }
                };
                
                // Add buttons to container
                buttonsContainer.appendChild(openButton);
                buttonsContainer.appendChild(cancelButton);
                
                // Add container to modal
                progressModal.appendChild(buttonsContainer);
              }
              
              resolve();
            } else {
              reject(new Error('Failed to load document'));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send();
        });
      } else {
        // For smaller files, use the simpler fetch approach
        const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${documentId}/download`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Download-Action': 'view'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to load document');
        }
        
        // Get the blob from response
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and trigger click to avoid popup blockers
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        
        // For PDFs, try to open in browser; for other files, download
        const isPDF = blob.type === 'application/pdf';
        if (!isPDF) {
          link.download = documentInfo.original_name || 'document';
        }
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Failed to open document. Please try again.');
      
      if (progressModal && progressModal.parentNode) {
        document.body.removeChild(progressModal);
      }
      
      // Clean up blob URL if it exists
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    }
  };

  // Mobile-friendly open handler - opens document directly and lets OS handle it
  const handleMobileOpen = async (documentInfo = {}) => {
    try {
      const token = localStorage.getItem('rwcvault_token');

      const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${documentInfo.id}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Download-Action': 'view'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to open document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // On mobile, navigate to the blob URL directly
      // This lets the mobile OS handle the file natively
      window.location.href = url;

    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document. Please try again.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format filename by replacing dashes with spaces and cleaning up
  const formatFilename = (filename) => {
    if (!filename) return '';
    return filename.replace(/-/g, ' ').trim();
  };

  // Sort items based on current sorting criteria
  const sortItems = (items, type = 'documents') => {
    if (!items || items.length === 0) return items;
    
    const sorted = [...items].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'name':
          valueA = getMonthSortValue(type === 'documents' ? a.original_name : a.name) || '';
          valueB = getMonthSortValue(type === 'documents' ? b.original_name : b.name) || '';
          break;
        case 'date':
          if (type === 'documents') {
            valueA = new Date(a.uploaded_at || a.created_at || 0);
            valueB = new Date(b.uploaded_at || b.created_at || 0);
          } else {
            valueA = new Date(a.created_at || 0);
            valueB = new Date(b.created_at || 0);
          }
          break;
        case 'size':
          if (type === 'documents') {
            valueA = a.file_size || 0;
            valueB = b.file_size || 0;
          } else {
            // Folders don't have size, sort by name instead
            valueA = a.name || '';
            valueB = b.name || '';
          }
          break;
        default:
          valueA = getMonthSortValue(type === 'documents' ? a.original_name : a.name) || '';
          valueB = getMonthSortValue(type === 'documents' ? b.original_name : b.name) || '';
      }
      
      // Handle string comparison
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      // Handle numeric/date comparison
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change sort field and default to ascending
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const renderFolderTree = (foldersToRender, level = 0) => {
    if (level === 0 && foldersToRender.length > 0 && !foldersToRender[0].children) {
      foldersToRender = buildFolderTree(foldersToRender);
    }

    // Sort folders alphabetically by name (A-Z) with smart month detection
    const sortedFolders = [...foldersToRender].sort((a, b) => {
      const nameA = getMonthSortValue(a.name || '');
      const nameB = getMonthSortValue(b.name || '');
      return nameA.localeCompare(nameB);
    });

    return sortedFolders.map(folder => {
      const hasChildren = folder.children && folder.children.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const isSelected = selectedFolder === folder.id;

      return (
        <div key={folder.id} style={{ marginLeft: `${level * 12}px` }}>
          <div
            className={`p-2 flex items-center gap-2 rounded-md transition-all duration-200 text-sm min-w-0 w-full
              ${isSelected 
                ? 'bg-blue-100 text-rwc-blue font-medium border-l-4 border-rwc-blue' 
                : 'text-gray-700 hover:bg-gray-50'
              }`}
          >
            {/* Expand/Collapse button */}
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolderExpansion(folder.id);
                }}
                className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded transition-colors"
              >
                <svg 
                  className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            ) : (
              <div className="w-5 h-5 flex items-center justify-center">
                <div className="w-1 h-1 rounded-full bg-gray-400"></div>
              </div>
            )}
            
            {/* Folder icon and name */}
            <div 
              onClick={() => handleFolderClick(folder.id)}
              className="flex items-center gap-2 flex-1 cursor-pointer min-w-0"
              title={folder.name}
            >
              <span className="text-base flex-shrink-0">📁</span>
              <span className="truncate text-sm font-medium min-w-0 block">{folder.name}</span>
            </div>
          </div>
          
          {/* Render children if expanded */}
          {hasChildren && isExpanded && (
            <div className="ml-2">
              {renderFolderTree(folder.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  const getRootFolders = () => {
    let rootFolders;
    if (folders.length > 0 && folders[0].children !== undefined) {
      rootFolders = folders;
    } else {
      rootFolders = folders.filter(folder => !folder.parent_id);
    }
    return sortItems(rootFolders, 'folders');
  };

  const displayDocuments = sortItems(documents, 'documents');

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <h1 className="text-xl font-semibold text-gray-900">Mutual Vault</h1>
            
            {/* Desktop user actions */}
            <div className="hidden md:flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user.first_name || user.username}</span>
              <button
                onClick={onProfileClick}
                className="btn-secondary text-sm"
              >
                My Profile
              </button>
              {isAdmin && (
                <button
                  onClick={onAdminClick}
                  className="btn-primary text-sm"
                >
                  Admin Dashboard
                </button>
              )}
              <button onClick={onLogout} className="text-sm text-red-600 hover:text-red-700 underline">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="mobile-overlay md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Desktop always visible, Mobile drawer */}
          <aside className={`
            fixed md:relative inset-y-0 left-0 z-40 w-64 bg-white shadow-lg md:shadow-sm 
            transform transition-transform duration-300 ease-in-out md:transform-none
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            md:w-64 md:flex-shrink-0 border-r border-gray-200 md:border-0 rounded-lg md:rounded-lg
          `}>
            <div className="p-4 h-full overflow-y-auto">
              {/* Mobile close button */}
              <div className="flex justify-between items-center mb-4 md:hidden">
                <h2 className="text-lg font-semibold text-gray-900">Folders</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Desktop header */}
              <div className="hidden md:flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  📁 Folders
                </h2>
                <button
                  onClick={() => {
                    setLoading(true);
                    fetchFolders().then(() => setLoading(false));
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                  title="Refresh folders"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>

              {/* Folder tree */}
              {loading && !folders.length ? (
                <p className="text-gray-500 text-sm">Loading...</p>
              ) : folders.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">
                  No folders available
                </p>
              ) : (
                <div className="space-y-1">
                  {renderFolderTree(folders)}
                </div>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
              {/* Content Header */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                {/* Breadcrumb Navigation */}
                <div className="flex items-center gap-2">
                  {/* Back Button */}
                  {!showingRootFolders && selectedFolder && (
                    <button
                      onClick={() => {
                        const breadcrumbPath = buildBreadcrumbPath(selectedFolder);
                        if (breadcrumbPath.length > 1) {
                          // Navigate to parent folder
                          const parentFolder = breadcrumbPath[breadcrumbPath.length - 2];
                          handleFolderClick(parentFolder.id);
                        } else {
                          // Navigate back to root
                          handleBackToFolders();
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 hover:text-rwc-blue bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      title="Go back"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back
                    </button>
                  )}
                  
                  {/* Breadcrumb Path */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 overflow-x-auto">
                    {showingRootFolders ? (
                      <span className="font-medium text-gray-900">All Folders</span>
                    ) : selectedFolder ? (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {/* Root link */}
                        <button
                          onClick={handleBackToFolders}
                          className="text-rwc-blue hover:text-rwc-blue-dark hover:underline"
                        >
                          All Folders
                        </button>
                        
                        {/* Breadcrumb path segments */}
                        {buildBreadcrumbPath(selectedFolder).map((folder, index, array) => (
                          <React.Fragment key={folder.id}>
                            <span className="text-gray-400">/</span>
                            {index === array.length - 1 ? (
                              // Current folder - not clickable
                              <span className="font-medium text-gray-900">{folder.name}</span>
                            ) : (
                              // Parent folders - clickable
                              <button
                                onClick={() => handleFolderClick(folder.id)}
                                className="text-rwc-blue hover:text-rwc-blue-dark hover:underline"
                              >
                                {folder.name}
                              </button>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <span className="font-medium text-gray-900">Documents</span>
                    )}
                  </div>
                </div>

                {/* Controls - View Toggle and Sort */}
                <div className="flex items-center gap-4">
                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Sort:</span>
                    <div className="relative">
                      <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                          const [field, order] = e.target.value.split('-');
                          setSortBy(field);
                          setSortOrder(order);
                        }}
                        className="text-sm border border-gray-300 rounded-md px-3 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-rwc-blue focus:border-transparent"
                      >
                        <option value="name-asc">Name (A-Z)</option>
                        <option value="name-desc">Name (Z-A)</option>
                        <option value="date-desc">Date (Newest)</option>
                        <option value="date-asc">Date (Oldest)</option>
                        <option value="size-desc">Size (Largest)</option>
                        <option value="size-asc">Size (Smallest)</option>
                      </select>
                    </div>
                  </div>

                  {/* View Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">View:</span>
                    <div className="flex rounded-md overflow-hidden border border-gray-300">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-1 text-sm font-medium transition-colors ${
                          viewMode === 'grid' 
                            ? 'bg-rwc-blue text-white' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Grid
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-1 text-sm font-medium transition-colors ${
                          viewMode === 'list' 
                            ? 'bg-rwc-blue text-white' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        List
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="text-gray-500">Loading...</div>
                </div>
              ) : showingRootFolders ? (
                // Show folders in main area when at root
                getRootFolders().length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4 opacity-30">📁</div>
                    <p className="text-gray-500">No folders available</p>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {getRootFolders().map(folder => (
                      <div
                        key={folder.id}
                        onClick={() => handleFolderClick(folder.id)}
                        className="folder-card"
                      >
                        <div className="flex flex-col items-center gap-3 p-4">
                          <div className="text-4xl">📁</div>
                          <div className="font-medium text-gray-900 text-center text-sm leading-tight">{folder.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {getRootFolders().map(folder => (
                      <div
                        key={folder.id}
                        onClick={() => handleFolderClick(folder.id)}
                        className="folder-card"
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-2xl">📁</div>
                          <div>
                            <div className="font-medium text-gray-900">{folder.name}</div>
                            <div className="text-sm text-gray-500">Folder</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // Show documents and subfolders
                <>
                  {(() => {
                    const subfolders = selectedFolder ? sortItems(getSubfolders(selectedFolder), 'folders') : [];
                    const hasContent = subfolders.length > 0 || displayDocuments.length > 0;

                    if (!hasContent) {
                      return (
                        <div className="text-center py-12">
                          <div className="text-4xl mb-4 opacity-30">📁</div>
                          <p className="text-gray-500">This folder is empty</p>
                        </div>
                      );
                    }

                    if (viewMode === 'grid') {
                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {/* Subfolders */}
                          {subfolders.map(folder => (
                            <div
                              key={`folder-${folder.id}`}
                              onClick={() => handleFolderClick(folder.id)}
                              className="folder-card"
                            >
                              <div className="flex flex-col items-center gap-3 p-4">
                                <div className="text-4xl">📁</div>
                                <div className="font-medium text-gray-900 text-center text-sm leading-tight">{folder.name}</div>
                              </div>
                            </div>
                          ))}

                          {/* Documents */}
                          {displayDocuments.map(doc => {
                            const formattedName = formatFilename(doc.original_name);
                            return (
                              <div
                                key={`doc-${doc.id}`}
                                className="document-card group"
                              >
                                <div className="flex flex-col h-full p-4">
                                  {/* Document Icon */}
                                  <div className="flex justify-center mb-3">
                                    <div className="w-16 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-2xl group-hover:bg-gray-200 transition-colors">
                                      📄
                                    </div>
                                  </div>
                                  
                                  {/* Document Info */}
                                  <div className="flex-1 flex flex-col">
                                    <div 
                                      className="font-medium text-gray-900 text-sm leading-tight mb-2 break-words"
                                      title={formattedName}
                                    >
                                      {formattedName}
                                    </div>
                                    <div className="text-xs text-gray-500 mb-4">
                                      {formatFileSize(doc.file_size)}
                                    </div>
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    {isMobile ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMobileOpen(doc);
                                        }}
                                        className="text-sm py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex-1"
                                      >
                                        Open
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            viewDocument(doc.id, doc);
                                          }}
                                          className="text-sm py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex-1"
                                        >
                                          View
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDirectDownload(doc);
                                          }}
                                          className="text-sm py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors flex-1"
                                        >
                                          Download
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      return (
                        <div className="space-y-2">
                          {/* Subfolders */}
                          {subfolders.map(folder => (
                            <div
                              key={`folder-${folder.id}`}
                              onClick={() => handleFolderClick(folder.id)}
                              className="folder-card"
                            >
                              <div className="flex items-center gap-4">
                                <div className="text-2xl">📁</div>
                                <div>
                                  <div className="font-medium text-gray-900">{folder.name}</div>
                                  <div className="text-sm text-gray-500">Folder</div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Documents */}
                          {displayDocuments.map(doc => {
                            const formattedName = formatFilename(doc.original_name);
                            return (
                              <div
                                key={`doc-${doc.id}`}
                                className="document-card"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-lg flex-shrink-0">
                                    📄
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900" title={formattedName}>
                                      {formattedName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {formatFileSize(doc.file_size)}
                                    </div>
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                                    {isMobile ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMobileOpen(doc);
                                        }}
                                        className="text-sm py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                                      >
                                        Open
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            viewDocument(doc.id, doc);
                                          }}
                                          className="text-sm py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                                        >
                                          View
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDirectDownload(doc);
                                          }}
                                          className="text-sm py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                                        >
                                          Download
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                  })()}
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Navigation */}
      <MobileNav
        onMenuClick={() => setSidebarOpen(true)}
        onProfileClick={onProfileClick}
        onAdminClick={onAdminClick}
        onLogout={onLogout}
        isAdmin={isAdmin}
      />
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenFromUrl = urlParams.get('rwc_reset_token');
    
    if (resetTokenFromUrl) {
      setResetToken(resetTokenFromUrl);
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('rwcvault_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        if (data.user.password_change_required) {
          setRequiresPasswordChange(true);
          return;
        } else {
          setRequiresPasswordChange(false);
        }

        await checkAdminStatus();
      } else {
        localStorage.removeItem('rwcvault_token');
        localStorage.removeItem('rwcvault_remember');
        setUser(null);
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Token verification failed:', err);
      if (err.message !== 'Failed to fetch') {
        localStorage.removeItem('rwcvault_token');
        localStorage.removeItem('rwcvault_remember');
        setUser(null);
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkAdminStatus = async (specificToken = null) => {
    try {
      const token = specificToken || localStorage.getItem('rwcvault_token');


      setIsAdmin(false);

      if (!token) {
        return false;
      }

      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        const data = await response.json();
        setIsAdmin(true);
        return true;
      } else {
        setIsAdmin(false);
        return false;
      }
    } catch (err) {
      console.error('Admin check failed:', err);
      setIsAdmin(false);
      return false;
    }
  };

  const handleLogin = async (userData, token) => {

    setIsAdmin(false);
    setShowAdminDashboard(false);
    setShowProfile(false);
    setRequiresPasswordChange(false);

    setUser(userData);
    
    if (userData.password_change_required) {
      setRequiresPasswordChange(true);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    await checkAdminStatus(token);
  };

  const handlePasswordChanged = async () => {
    
    setRequiresPasswordChange(false);
    
    if (user) {
      setUser({
        ...user,
        password_change_required: false
      });
    }
    
    await checkAdminStatus();
  };

  const handleLogout = async () => {

    const token = localStorage.getItem('rwcvault_token');
    if (token) {
      try {
        await fetch(`${window.rwcvaultData.apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error('Logout API call failed:', err);
      }
    }

    localStorage.removeItem('rwcvault_token');
    localStorage.removeItem('rwcvault_remember');

    setUser(null);
    setIsAdmin(false);
    setShowAdminDashboard(false);
    setShowProfile(false);
    setRequiresPasswordChange(false);
    setLoading(false);

  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Handle password reset flow
  if (resetToken && !resetSuccess) {
    return <ResetPassword 
      token={resetToken} 
      onSuccess={() => {
        setResetSuccess(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      }} 
    />;
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Password Reset Successful!</h2>
            <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg mb-6 text-sm">
              Your password has been successfully reset. You can now log in with your new password.
            </div>
            <button 
              onClick={() => {
                setResetToken(null);
                setResetSuccess(false);
              }}
              className="w-full btn-primary"
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  if (requiresPasswordChange && user) {
    return <MandatoryPasswordChange user={user} onPasswordChanged={handlePasswordChanged} />;
  }

  if (showAdminDashboard && isAdmin) {
    return <AdminDashboard
      user={user}
      onBack={() => setShowAdminDashboard(false)}
    />;
  }

  if (showProfile && user) {
    return <UserProfile 
      user={user} 
      onBack={() => setShowProfile(false)}
      onUserUpdate={(updatedUser) => setUser(updatedUser)}
    />;
  }

  return (
    <DocumentPortal
      user={user}
      onLogout={handleLogout}
      isAdmin={isAdmin}
      onAdminClick={() => setShowAdminDashboard(true)}
      onProfileClick={() => setShowProfile(true)}
    />
  );
}

export default App;